import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../../singleton/axios';
import { Watchlist } from './useWatchlists';

async function fetchWatchlistDetail(watchlistId: string): Promise<Watchlist> {
  const { data } = await api.get<{ data: Watchlist }>(`/stock-watchlists/${watchlistId}`);
  return data.data;
}

export function useWatchlistDetail(watchlistId: string | null) {
  const query = useQuery({
    queryKey: ['watchlist', watchlistId],
    queryFn: () => fetchWatchlistDetail(watchlistId!),
    enabled: !!watchlistId, // Only run query if watchlistId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter than list since details change more frequently)
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors or 404s
      if (error?.message?.includes('401') || 
          error?.message?.includes('403') || 
          error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Handle errors using useEffect pattern
  React.useEffect(() => {
    if (query.error) {
      // Show error notification only for unexpected errors (not auth or 404)
      if (!query.error?.message?.includes('401') &&
          !query.error?.message?.includes('403') &&
          !query.error?.message?.includes('404')) {
        enqueueSnackbar('Failed to load watchlist details', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    }
  }, [query.error]);

  return query;
}